/**
 * layout — the project page's binding of the shared detail-page surface.
 *
 * A project has these panes over its canvas:
 *
 *   context     who owns it, who is on it, when it is due       (left)
 *   resources   what is linked to it, counted by type           (right)
 *   access      the collaborator list and the way to change it  (right)
 *   economics   how the project is priced, and the way to edit  (right,
 *               SWITCHED OFF — see `PROJECT_ECONOMICS_ENABLED`)
 *   milestones  the phases and their progress    (right, SWITCHED OFF — see
 *               `PROJECT_MILESTONES_ENABLED`)
 *   activity    the audit log                                   (right)
 *
 * The centre is the CANVAS — the tab bar and whichever tab is open (Details,
 * Resources, Feed) — always present, never tiled, with the panes floating
 * over it.
 *
 * These were a hardcoded `lg:col-span-2` column that only existed on the
 * Details tab, plus a hand-rolled `fixed inset-y-0 right-0 w-96` drawer with
 * its own scrim for the audit log. Both are gone: they are panes now, so they
 * dock, stack, reorder, resize and persist like every other pane in the app,
 * and they stay to hand while the canvas is showing Resources or the Feed.
 *
 * WHY ENTITY SCOPE. Same reason as matters: a project run from its linked
 * resources and one run from its feed want different panes open, and one
 * shared key would make every project inherit the arrangement of whichever
 * was opened last. So the project uuid is part of the storage key.
 *
 * NO LEGACY KEYS. This surface is new — nothing was ever written under an
 * unscoped key for it, so there is nothing to migrate.
 *
 * NO READER PANE. There was one, closed by default, with an empty component
 * behind it, on the theory that the first thing wanting to show a document in
 * place would find it ready. That is a pane that can open onto nothing and a
 * file that exists to be empty. A pane costs one config entry and one body
 * whenever something does want it — so it will be added then, by the change
 * that has content to put in it.
 */

import {
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
} from '../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../shared/surface/defineDetailSurface';

export type ProjectPanelId =
  | 'context'
  | 'resources'
  | 'access'
  | 'economics'
  | 'milestones'
  | 'activity';

/**
 * MILESTONES ARE OFF.
 *
 * Switched off deliberately, not deleted: the feature is not ready to be the
 * first thing someone meets on a project, so it leaves the pane set and the
 * rail. Everything behind it — the pane config below, `ProjectMilestones`,
 * the endpoints, the pane entry in `ProjectDetail` — stays intact and comes
 * back by flipping this ONE constant to `true`.
 */
export const PROJECT_MILESTONES_ENABLED = false;

/**
 * ECONOMICS IS OFF (proj-7 — DEFERRED).
 *
 * Project pricing shipped in wave 3 and is being held back: the fields are not
 * stored by the backend yet, so a price set here does not survive a reload.
 * Switched off the same way milestones are, not deleted — the pane config
 * below, `surface/ProjectEconomicsPane`, `economics/ProjectPricingPanel`, the
 * `projectPricing` side-panel type and the pane entry in `ProjectDetail` all
 * stay intact and come back by flipping this ONE constant to `true`.
 *
 * The SHARED pricing pieces (`shared/economics/pricingModel`, `PricingFields`)
 * are untouched — matters use them and are unaffected.
 */
export const PROJECT_ECONOMICS_ENABLED = false;

/** The canvas's tabs. Labels are the page's — see `ProjectDetail`. */
export type ProjectTab = 'Details' | 'Resources' | 'Feed';

export const PROJECT_TABS: ProjectTab[] = ['Details', 'Resources', 'Feed'];

export type ProjectPanelSlot = GenericSlot<ProjectPanelId>;
export type ProjectPanelLayout = GenericLayout<ProjectPanelId>;

export const PROJECT_PANEL_IDS: ProjectPanelId[] = [
  'context',
  'resources',
  'access',
  ...(PROJECT_ECONOMICS_ENABLED ? (['economics'] as ProjectPanelId[]) : []),
  ...(PROJECT_MILESTONES_ENABLED ? (['milestones'] as ProjectPanelId[]) : []),
  'activity',
];

/**
 * Every pane: its label, its resize limits, where it starts, whether it opens
 * on a first visit and whether it gets a rail toggle.
 *
 * TWO ARE OPEN BY DEFAULT — context and resources — because those are exactly
 * what the old page showed without being asked: the facts on the left of the
 * Details tab and the linked-resource summary at the top of its right column.
 * Opening on the defaults nobody chose is how a returning user loses their
 * bearings, so the pane set starts where the fixed layout ended.
 */
const PROJECT_PANES: Record<ProjectPanelId, DetailPaneConfig> = {
  // The facts of the project, read against whatever tab is open — which is
  // the whole reason it moved out of the Details tab.
  context: {
    label: 'Context',
    bounds: { min: 260, max: 720 },
    dock: 'left', index: 0, width: 320, visible: true, rail: true,
  },
  resources: {
    label: 'Resources',
    bounds: { min: 240, max: 720 },
    dock: 'right', index: 0, width: 320, visible: true, rail: true,
  },
  access: {
    label: 'Access',
    bounds: { min: 240, max: 640 },
    dock: 'right', index: 1, width: 300, visible: false, rail: true,
  },
  // Pricing, read against whatever tab is open — the same fields a matter
  // carries, in the same order. See `shared/economics`.
  economics: {
    label: 'Economics',
    bounds: { min: 260, max: 640 },
    dock: 'right', index: 2, width: 320, visible: false, rail: true,
  },
  // Milestones carry an inline add/edit form, so they need a wider floor than
  // a list of counts before the row stops wrapping onto four lines.
  milestones: {
    label: 'Milestones',
    bounds: { min: 280, max: 720 },
    dock: 'right', index: 3, width: 340, visible: false, rail: true,
  },
  activity: {
    label: 'Activity',
    bounds: { min: 280, max: 760 },
    dock: 'right', index: 4, width: 340, visible: false, rail: true,
  },
};

export const PROJECT_SURFACE = defineDetailSurface<ProjectPanelId, ProjectTab>({
  kind: 'detail',
  // Also the `data-testid` prefix for the shell: `project-surface`, etc.
  name: 'project',
  scope: 'entity',
  ids: PROJECT_PANEL_IDS,
  panes: PROJECT_PANES,
  tabs: PROJECT_TABS,
});

/* ── derived, for call sites and tests that want the plain records ── */

export const PROJECT_PANEL_LABELS: Record<ProjectPanelId, string> = PROJECT_SURFACE.labels;
export const PROJECT_PANEL_BOUNDS: Record<ProjectPanelId, PanelBounds> = PROJECT_SURFACE.bounds;
export const PROJECT_DEFAULT_LAYOUT: ProjectPanelLayout = PROJECT_SURFACE.defaults;
